// /app/more — mobile-only "Everything else" sheet.
//
// On phones the bottom-tab bar surfaces the 5 most-used flows (Home,
// Calendar, Generate, Inbox, More). The remaining surfaces (Analytics,
// Bulk, Integrations, Team, Audit, Settings) live here, behind one tap.
// On desktop the user reaches them from the sidebar, so this route is
// only intended for mobile + visible only there.

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useSignOut } from "@/lib/auth";
import { api } from "@/lib/api";
import { Icons } from "@/components/icons";
import type { MeResponse } from "@/lib/auth";

export const Route = createFileRoute("/app/more")({
  component: MorePage,
});

type Item = {
  to?: string;
  icon: keyof typeof Icons;
  labelTr: string;
  labelEn: string;
  subTr?: string;
  subEn?: string;
  onClick?: () => void;
};

function MorePage() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();
  const signOut = useSignOut();

  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/auth/me"),
    staleTime: 60_000,
  });

  const items: Item[] = [
    {
      to: "/app/analytics",
      icon: "BarChart",
      labelTr: "İstatistikler",
      labelEn: "Analytics",
      subTr: "Markaların performansı",
      subEn: "How your brands perform",
    },
    {
      to: "/app/integrations",
      icon: "Plug",
      labelTr: "Entegrasyonlar",
      labelEn: "Integrations",
      subTr: "Sosyal hesaplar",
      subEn: "Connected social accounts",
    },
    {
      to: "/app/billing",
      icon: "Zap",
      labelTr: "Abonelik",
      labelEn: "Billing",
      subTr: "Plan + krediler",
      subEn: "Plan + credits",
    },
    {
      to: "/app/audit",
      icon: "Shield",
      labelTr: "İşlem Geçmişi",
      labelEn: "Activity log",
      subTr: "Yayın geçmişi · 30 gün",
      subEn: "Publish history · 30 days",
    },
    {
      to: "/app/settings",
      icon: "Settings",
      labelTr: "Ayarlar",
      labelEn: "Settings",
      subTr: "Workspace · KVKK · e-Fatura",
      subEn: "Workspace · KVKK · e-Invoice",
    },
  ];

  return (
    <div style={{ padding: "16px 18px 32px" }}>
      <div className="eyebrow" style={{ marginBottom: 8 }}>
        — {tr ? "Daha" : "More"}
      </div>
      <h1
        style={{
          fontFamily: "var(--display)",
          fontSize: 26,
          fontWeight: 700,
          letterSpacing: "-0.02em",
          marginBottom: 18,
        }}
      >
        {tr ? "Her şey burada." : "Everything else."}
      </h1>

      {/* User card */}
      <div
        className="card"
        style={{
          padding: 14,
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 18,
          background: "var(--surface)",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "var(--accent-soft)",
            color: "var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--display)",
            fontWeight: 700,
            fontSize: 14,
          }}
        >
          {(me?.user?.display_name ?? me?.user?.email ?? "?").slice(0, 2).toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {me?.user?.display_name ?? me?.user?.email ?? "—"}
          </div>
          <div
            className="mono"
            style={{ fontSize: 10, color: "var(--mu)" }}
          >
            {me?.workspace?.name ?? "—"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item) => {
          const Ic = Icons[item.icon];
          const inner = (
            <>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: "var(--base)",
                  border: "1px solid var(--b1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--tx2)",
                  flexShrink: 0,
                }}
              >
                <Ic size={16} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {tr ? item.labelTr : item.labelEn}
                </div>
                {item.subTr || item.subEn ? (
                  <div style={{ fontSize: 11, color: "var(--mu)", marginTop: 2 }}>
                    {tr ? item.subTr : item.subEn}
                  </div>
                ) : null}
              </div>
              <Icons.ChevronRight size={14} style={{ color: "var(--mu)" }} />
            </>
          );
          const sharedStyle = {
            width: "100%",
            padding: 14,
            background: "var(--surface)",
            border: "1px solid var(--b1)",
            borderRadius: 10,
            color: "var(--tx)",
            display: "flex",
            alignItems: "center",
            gap: 12,
            textAlign: "left" as const,
            textDecoration: "none",
          };
          if (item.to) {
            return (
              <Link key={item.icon + item.labelEn} to={item.to} style={sharedStyle}>
                {inner}
              </Link>
            );
          }
          return (
            <button
              key={item.icon + item.labelEn}
              type="button"
              onClick={item.onClick}
              style={sharedStyle}
            >
              {inner}
            </button>
          );
        })}
      </div>

      {/* Logout */}
      <button
        type="button"
        onClick={async () => {
          await signOut.mutateAsync();
          navigate({ to: "/sign-in" });
        }}
        disabled={signOut.isPending}
        style={{
          marginTop: 18,
          width: "100%",
          padding: 12,
          background: "transparent",
          border: "1px solid var(--b1)",
          borderRadius: 10,
          color: "var(--error)",
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        {signOut.isPending
          ? (tr ? "Çıkış yapılıyor…" : "Signing out…")
          : (tr ? "Çıkış yap" : "Sign out")}
      </button>
    </div>
  );
}
